import { useMemo } from "react";
import { GovRelease, GovSettingsOn } from "../../../../utils/icGovernance";
import { useGovernance } from "../GovernanceLayout";
import { SignOffChip, clientLabel, dateOnly, linkedIds, releaseTypeLabel, vendorLabel } from "../governanceStatus";
import { IdLink, LinkedIdList, LongText, PlainText, TitleLink } from "./cells";
import RegisterTable, { RegisterColumn, WaitingNotice } from "./RegisterTable";
import { useRegisterItems } from "./useRegisterItems";

// Release and Update Plan — columns in the client's workbook order.
const releaseColumns = (s: GovSettingsOn): RegisterColumn<GovRelease>[] => [
  { key: "id", header: "Release ID", render: (r) => <IdLink item={r} /> },
  { key: "version", header: "Version", render: (r) => <PlainText value={r.version} /> },
  { key: "releaseType", header: "Release Type", render: (r) => <PlainText value={r.releaseType ? releaseTypeLabel(r.releaseType) : null} /> },
  { key: "title", header: "Update Summary", wrap: true, minWidth: 240, render: (r) => <TitleLink item={r} /> },
  {
    key: "related",
    header: "Related CR / Issue IDs",
    render: (r) => <LinkedIdList ids={linkedIds(r, ["CHANGE_REQUEST", "ISSUE"])} />,
  },
  { key: "clientOwner", header: `${clientLabel(s)} Release Owner`, render: (r) => <PlainText value={r.clientOwner} /> },
  { key: "vendorOwner", header: `${vendorLabel(s)} Release Lead`, render: (r) => <PlainText value={r.vendorOwner} /> },
  { key: "devComplete", header: "Dev Complete", render: (r) => dateOnly(r.milestones?.devComplete) },
  { key: "qaComplete", header: `${vendorLabel(s)} QA Complete`, render: (r) => dateOnly(r.milestones?.qaComplete) },
  { key: "uatStart", header: "UAT Start", render: (r) => dateOnly(r.milestones?.uatStart) },
  { key: "uatComplete", header: "UAT Complete", render: (r) => dateOnly(r.milestones?.uatComplete) },
  { key: "production", header: "Production Date", render: (r) => dateOnly(r.milestones?.production) },
  { key: "downtime", header: "Expected Downtime", wrap: true, minWidth: 160, render: (r) => <LongText value={r.expectedDowntime} /> },
  {
    key: "communication",
    header: "User Communication",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.userCommunication} />,
  },
  {
    key: "rollback",
    header: "Backup / Rollback Plan",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.rollbackPlan} />,
  },
  {
    key: "validation",
    header: "Post-Deploy Validation",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.postDeployValidation} />,
  },
  { key: "approvalBy", header: `${clientLabel(s)} Approval By`, render: (r) => <PlainText value={r.signOff?.decidedBy} /> },
  { key: "approvalStatus", header: "Approval Status", render: (r) => <SignOffChip status={r.signOff?.status} /> },
];

const WAITING: WaitingNotice<GovRelease> = {
  reason: (r) => (r.signOff?.status === "PENDING" ? "approval" : null),
  describe: (n) => `${n} ${n === 1 ? "release waits" : "releases wait"} for your approval`,
};

const ReleaseRegister = () => {
  const { settings } = useGovernance();
  const register = useRegisterItems<GovRelease>("RELEASE");
  const columns = useMemo(() => releaseColumns(settings), [settings]);
  return (
    <RegisterTable
      {...register}
      kind="RELEASE"
      columns={columns}
      noun={{ one: "release", many: "releases" }}
      caption={`${vendorLabel(settings)} plans and deploys; ${clientLabel(settings)} approves`}
      waiting={WAITING}
    />
  );
};

export default ReleaseRegister;
